import { Injectable } from '@angular/core';
import { mapToVoid } from '@examdojo/core/rxjs';
import { ErrorHandlerService } from '@examdojo/error-handling';
import kebabCase from 'lodash/kebabCase';
import { finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { AssignmentBuilderQuery, AssignmentBuilderService } from '../assignment-builder';
import { ClassAssignmentHttpService } from './class-assignment-http.service';
import {
  ClassAssignmentHttpModel,
  ClassAssignmentStatus,
  ClassAssignmentStoreModel,
  CreateAssignmentData,
} from './class-assignment.model';
import { ClassAssignmentQuery } from './class-assignment.query';
import { ClassAssignmentStore } from './class-assignment.store';
import { LearningAssignmentHttpService } from './learning-assignment-http.service';

@Injectable({ providedIn: 'root' })
export class ClassAssignmentService {
  constructor(
    private readonly store: ClassAssignmentStore,
    private readonly query: ClassAssignmentQuery,
    private readonly classAssignmentHttpService: ClassAssignmentHttpService,
    private readonly learningAssignmentHttp: LearningAssignmentHttpService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly assignmentBuilderQuery: AssignmentBuilderQuery,
    private readonly assignmentBuilderService: AssignmentBuilderService,
  ) {}

  init(classId?: string) {
    this.store.setLoading(true);

    return this.classAssignmentHttpService.fetchAllAssignmentsView({ classId }).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'fetch' }),
      map((assignments) => assignments.map((a) => this.mapToStoreModel(a))),
      tap((assignments) => this.store.set(assignments)),
      finalize(() => this.store.setLoading(false)),
    );
  }

  createAssignment(data: CreateAssignmentData) {
    return this.classAssignmentHttpService.createAssignment(data).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'create' }),
      switchMap((assignmentId) =>
        this.classAssignmentHttpService.fetchAssignment(assignmentId).pipe(
          map((assignment) => {
            const storeModel = this.mapToStoreModel(assignment);
            this.store.create(storeModel);
            return assignmentId;
          }),
        ),
      ),
    );
  }

  duplicateAssignment(sourceAssignmentId: string) {
    return this.classAssignmentHttpService.duplicateAssignment(sourceAssignmentId).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'create' }),
      switchMap((assignmentId) =>
        this.classAssignmentHttpService.fetchAssignment(assignmentId).pipe(
          map((assignment) => {
            const storeModel = this.mapToStoreModel(assignment);
            this.store.create(storeModel);
            return storeModel;
          }),
        ),
      ),
    );
  }

  deleteAssignment(assignmentId: string) {
    return this.classAssignmentHttpService.delete(assignmentId).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'delete' }),
      tap(() => this.store.delete(assignmentId)),
    );
  }

  fetchAssignmentStudents(assignmentId: string) {
    return this.classAssignmentHttpService
      .fetchAssignmentStudents(assignmentId)
      .pipe(this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'fetch' }));
  }

  fetchTopicAnalysis(assignmentId: string) {
    return this.classAssignmentHttpService
      .fetchTopicAnalysis(assignmentId)
      .pipe(this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'fetch' }));
  }

  refreshAssignment(assignmentId: string): Observable<ClassAssignmentStoreModel> {
    return this.classAssignmentHttpService.fetchAssignment(assignmentId).pipe(
      map((assignment) => this.mapToStoreModel(assignment)),
      tap((assignment) => this.store.upsert(assignment.id, assignment)),
    );
  }

  setActiveAssignment(id: string | null) {
    this.store.setActive(id);
  }

  publishAssignment(dueAt: Date) {
    const { activeAssignment, assignmentId } = this.getActiveAssignmentOrThrow();

    const saveSectionsIfDirty$ = this.assignmentBuilderQuery.getProp('isDirty')
      ? this.assignmentBuilderService.saveDraft()
      : of(undefined);

    return saveSectionsIfDirty$.pipe(
      switchMap(() => this.classAssignmentHttpService.publishAssignment(assignmentId, dueAt)),
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'update' }),
      tap(() =>
        this.store.update(activeAssignment.id, {
          status: 'PUBLISHED',
          is_published: true,
          due_at: dueAt.toISOString(),
          scheduled_at: new Date().toISOString(),
        }),
      ),
      mapToVoid(),
    );
  }

  scheduleAssignment(scheduledAt: Date, dueAt: Date) {
    const { activeAssignment, assignmentId } = this.getActiveAssignmentOrThrow();

    const saveSectionsIfDirty$ = this.assignmentBuilderQuery.getProp('isDirty')
      ? this.assignmentBuilderService.saveDraft()
      : of(undefined);

    return saveSectionsIfDirty$.pipe(
      switchMap(() => this.classAssignmentHttpService.scheduleAssignment(assignmentId, scheduledAt, dueAt)),
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'update' }),
      tap(() =>
        this.store.update(activeAssignment.id, {
          status: 'SCHEDULED',
          scheduled_at: scheduledAt.toISOString(),
          due_at: dueAt.toISOString(),
        }),
      ),
      mapToVoid(),
    );
  }

  saveDraftChanges(dueAt: Date | null): Observable<void> {
    const { activeAssignment } = this.getActiveAssignmentOrThrow();
    const dueAtIso = dueAt?.toISOString() ?? null;

    return this.assignmentBuilderService.saveDraftWithDueDate(dueAt).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'update' }),
      tap(() => this.store.update(activeAssignment.id, { due_at: dueAtIso })),
      mapToVoid(),
    );
  }

  updateTitle(name: string): Observable<void> {
    const { activeAssignment, assignmentId } = this.getActiveAssignmentOrThrow();

    return this.learningAssignmentHttp.update(assignmentId, { name }).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'update' }),
      tap(() => this.store.update(activeAssignment.id, { name })),
      mapToVoid(),
    );
  }

  updateDueDate(dueAt: Date | null): Observable<void> {
    const { activeAssignment } = this.getActiveAssignmentOrThrow();
    const dueAtIso = dueAt?.toISOString() ?? null;

    if (activeAssignment.due_at === dueAtIso) {
      return of(undefined);
    }

    return this.classAssignmentHttpService.update(activeAssignment.id, { due_at: dueAtIso }).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'update' }),
      tap(() => this.store.update(activeAssignment.id, { due_at: dueAtIso })),
      mapToVoid(),
    );
  }

  setActiveBySlug(slug: ClassAssignmentStoreModel['slug']): boolean {
    const assignment = this.query.getAllUIEntities().find((a) => a.slug === slug);
    if (!assignment) {
      return false;
    }
    this.store.setActive(assignment.id);
    return true;
  }

  private getActiveAssignmentOrThrow() {
    const activeAssignment = this.query.getActive();
    if (!activeAssignment?.assignment_id) {
      throw new Error('No active assignment found');
    }
    return { activeAssignment, assignmentId: activeAssignment.assignment_id };
  }

  private mapToStoreModel(httpModel: ClassAssignmentHttpModel): ClassAssignmentStoreModel {
    const id = httpModel.id ?? '';
    const name = httpModel.name ?? '';
    const className = httpModel.class_name ?? '';

    return {
      ...httpModel,
      id,
      assignment_id: httpModel.assignment_id ?? '',
      name,
      slug: `${kebabCase(`${className} ${name}`)}-${id.slice(0, 8)}`,
      status: (httpModel.status as ClassAssignmentStatus) ?? 'DRAFT',
      class_id: httpModel.class_id ?? '',
      class_name: httpModel.class_name ?? '',
      question_count: httpModel.question_count ?? 0,
      section_count: httpModel.section_count ?? 0,
      total_marks: httpModel.total_marks ?? 0,
      total_students: httpModel.total_students ?? 0,
      completed_count: httpModel.completed_count ?? 0,
      in_progress_count: httpModel.in_progress_count ?? 0,
      estimated_duration_minutes: httpModel.estimated_duration_minutes ?? 0,
      has_unpublished_changes: httpModel.has_unpublished_changes ?? false,
      is_published: httpModel.is_published ?? false,
      created_at: httpModel.created_at ?? '',
      created_by: httpModel.created_by ?? '',
      updated_at: httpModel.updated_at ?? '',
    } as ClassAssignmentStoreModel;
  }
}
